import React, { RefObject, useEffect, useMemo, useRef } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, Paper, TableContainer, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment-timezone';
import VirtualizedTable from '../../virtualized-table';
import { AppState } from '../../../store';
import {
  getCommandsByDeviceKey,
  getFileTypeConfigs,
} from '../../../store/commands/actions';
import { headCells, headCellsAccessors } from './head-cells';
import { Command } from '../../../entities/commands-v1/command';
import useStyles from './styles';
import { useInterval } from '../../../globals/hooks/use-interval';
import { commandsActionNames } from '../../../store/commands/types';
import { formatDate } from '../../../globals/utils';

export interface FormattedRow {
  command?: string;
  id?: string;
  modifiedBy?: string;
  modifiedDateTime?: Date;
}

const ConfigurationTable = ({ onRowSelection, setQueueCount, queueCount }: { onRowSelection?: Function, setQueueCount?: Function, queueCount?: number }) => {
  const dispatch = useDispatch();
  const deviceKey = useSelector((state: AppState) => state.search.selectedEntity?.deviceKey);
  const commands: Command[] = useSelector((state: AppState) => state.commands.commands);
  const { fileTypeConfigs } = useSelector((state: AppState) => state.commands);
  const cursor = useSelector((state: AppState) => state.commands.nextCursor);
  const loading = useSelector((state: AppState) => state.bulkFlash.loading);
  const { timeFormat, dateFormat } = useSelector(
    (state: AppState) => state.userPreference.globalUserPreferences,
  );
  const timeZone = useSelector(
    (state: AppState) => state.userPreference.customUserPreferences.timeZone,
  );

  const tableContainer: RefObject<HTMLDivElement> = useRef(null);

  const data = useMemo(
    () =>
      commands?.map((command: Command) => {
        let fileTypeName: string | undefined = '';
        const fileTypeNameIndex: number = fileTypeConfigs.findIndex((f) => {
          if (command.fileTypeData && command.fileTypeData !== null) {
            return f.fileTypeId === `${command.fileTypeData}`;
          }
          return f.fileTypeId === `${command.fileType}`;
        });
        if (fileTypeNameIndex !== -1) {
          fileTypeName = fileTypeConfigs[fileTypeNameIndex].fileTypeName;
        } else {
          fileTypeName = 'File Type Unknown';
        }
        return {
          id: command.commandId,
          command: `# ${command.modifiedDateTime} - ${command.modifiedBy} - ${fileTypeName}`,
          modifiedDateTime: `${moment(command.modifiedDateTime)
            .tz(timeZone)
            .format(formatDate(dateFormat, timeFormat))}`,
          modifiedBy: command.modifiedBy,
        };
      }),
    [commands, fileTypeConfigs],
  );
  const columns = useMemo(() => headCells, []);
  const classes = useStyles();

  useEffect(() => {
    if (deviceKey) {
      dispatch({ type: commandsActionNames.SET_COMMANDS, payload: null });
      dispatch(getCommandsByDeviceKey({ deviceKey, statuses: '1' }));
      dispatch(getFileTypeConfigs());
    }
  }, [deviceKey]);

  const processRowSelection = (rowNumberObject: { [key: string]: boolean }[]) => {
    if (data && onRowSelection) {
      const selectedRows = Object.keys(rowNumberObject).map(
        (key: string) => data[Number.parseInt(key, 10)],
      );
      onRowSelection(selectedRows);
    }
  };

  const scrollHandler = () => {
    if (deviceKey && cursor !== '') {
      dispatch(
        getCommandsByDeviceKey({
          deviceKey,
          statuses: '1',
          cursor,
        }),
      );
    }
  };

  const crowHeight = 35;
  const rowsHeight = (data?.length || 0) * crowHeight + 86;
  const tableHeight = rowsHeight > 300 ? 300 : rowsHeight;

  if (setQueueCount && queueCount !== data?.length) {
    setQueueCount(data?.length || 0);
  }
  return (
    <div ref={tableContainer} className={classes.tableContainer}>
      {data ? (
        tableContainer && data.length > 0 ? (
          <Paper style={{ height: `${tableHeight - 23}px` }}>
            <CssBaseline>
              <TableContainer id="configure-table">
                <VirtualizedTable
                  initialSort="modifiedDateTime"
                  initialSortDirection="desc"
                  tableContainer={tableContainer}
                  columns={columns}
                  data={data}
                  cellHeight={crowHeight}
                  tableHeight={tableHeight}
                  useFrontEndSorting
                  itemCount={data.length}
                  loadMoreItems={scrollHandler}
                  onRowSelection={onRowSelection && processRowSelection}
                  loading={loading}
                  sortableAccessors={headCellsAccessors}
                />
              </TableContainer>
            </CssBaseline>
          </Paper>
        ) : (
          <Box pt={3} pb={1.5}>
            <Typography id="noCommandsMessage" variant="h5">
              No Commands currently in Queue
            </Typography>
          </Box>
        )
      ) : (
        'Loading...'
      )}
    </div>
  );
};

export default ConfigurationTable;
